import { useRef } from 'react';
import { Row, Col, Typography, getTypographyDefaultColor } from '@ag1/ui';
import clsx from 'clsx';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import useAddAccessibility from 'shared/hooks/addAccessibility';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { TabbedCollection } from 'types/studio/studio';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import styles from './TabbedCollectionSlide.module.scss';

type SlideActionType = {
  selected: boolean;
};

type TabbedCollectionItemProps = TabbedCollection['slides'][number];

const TabbedCollectionSlide = ({
  slideImage,
  slideText,
  slidePoints,
  selected,
  isAGZVariant,
  theme,
  visibleOnPageLoad
}: TabbedCollectionItemProps &
  SlideActionType &
  Pick<TabbedCollection, 'isAGZVariant' | 'theme'> & { visibleOnPageLoad: boolean }) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useAddAccessibility(contentRef, true);

  return (
    <div className={clsx(styles.item, selected && styles.itemSelected)}>
      <Row className={styles.row}>
        <Col span={{ xxs: 12, m: 6, l: { value: 4, wide: true }, xl: 4 }}>
          <Typography
            component="div"
            variant="body"
            weight="700"
            size="3"
            noPadding
            className={clsx(styles.slideTitleText, getThemeCSSClass(theme))}
            color={getTypographyDefaultColor(theme)}>
            <CustomPortableText value={slideText} />
          </Typography>
          <div ref={contentRef} className={styles.pointsWrapper}>
            {slidePoints &&
              slidePoints.map((item) => {
                const { _key: key } = item;
                return (
                  <div key={key} className={clsx(styles.point, getThemeCSSClass(theme))}>
                    <Typography
                      variant="body"
                      size="3"
                      weight="700"
                      noPadding
                      color={getTypographyDefaultColor(theme)}
                      className={clsx(styles.pointTitle, getThemeCSSClass(theme))}>
                      {item.slidePointTitle}
                    </Typography>
                    <Typography
                      component="div"
                      variant="body"
                      size="3"
                      weight="400"
                      noPadding
                      color={getTypographyDefaultColor(theme)}
                      className={clsx(
                        styles.pointText,
                        isAGZVariant && styles.isAGZ,
                        getThemeCSSClass(theme)
                      )}>
                      <CustomPortableText value={item.slidePointText} />
                    </Typography>
                  </div>
                );
              })}
          </div>
        </Col>
        <Col span={{ xxs: 12, m: 6 }}>
          {slideImage.image && (
            <StudioBaseImageV2
              image={slideImage.image}
              mobileImage={slideImage.mobileImage}
              tabletImage={slideImage.tabletImage}
              withMobileImage={slideImage.withMobileImage}
              className={styles.imageWrap}
              childClassName={styles.image}
              sizes={{ xxs: '249px', s: '426px', m: 6, l: '700px' }}
              eagerLoading={visibleOnPageLoad}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TabbedCollectionSlide;
